<template>
  <div class="css-editor">
    <div class="d-flex justify-content-end mb-2">
      <CButton color="link" size="sm" class="p-0" @click="showHelp = true">
        <CIcon name="cilInfo" class="mr-1"/>
        <translate translate-context="form_configurator.css">View examples</translate>
      </CButton>
    </div>
    <codemirror
        ref="cm"
        v-model="code"
        :options="cmOptions"
        @input="onInput"
    />
    <div v-if="validationError" class="invalid-feedback d-block mt-2">
      <CIcon name="cilWarning" class="mr-1"/>
      {{ validationError }}
    </div>

    <!-- Helper Modal -->
    <CModal
        :show.sync="showHelp"
        :title="$pgettext('form_configurator.css', 'CSS Examples')"
        size="lg"
        centered
    >
      <div class="css-examples">
        <p class="text-muted mb-3">
          <translate translate-context="form_configurator.css_help">
            Click on an example to copy it to the editor. You can combine multiple examples.
          </translate>
        </p>

        <div v-for="(example, index) in cssExamples" :key="index" class="example-card" @click="insertExample(example)">
          <div class="example-title">{{ example.title }}</div>
          <div class="example-description text-muted">{{ example.description }}</div>
          <pre class="example-code">{{ example.code }}</pre>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" @click="showHelp = false">
          <translate translate-context="form_configurator">Close</translate>
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import {codemirror} from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/css/css.js'
import 'codemirror/addon/edit/matchbrackets.js'
import 'codemirror/addon/edit/closebrackets.js'
import {debounce} from 'lodash'

// Dangerous CSS patterns that could be used for XSS or other attacks
const BLOCKED_PATTERNS = [
  /expression\s*\(/i,           // IE expression (XSS vector)
  /javascript\s*:/i,            // JavaScript URLs
  /behavior\s*:/i,              // IE behaviors
  /-moz-binding/i,              // Firefox XBL bindings
  /url\s*\(\s*["']?\s*data:/i,  // Data URLs (potential XSS)
  /@import/i,                   // External imports (security risk)
  /position\s*:\s*fixed/i,      // Could overlay entire page
]

export default {
  name: 'CssEditor',
  components: {codemirror},
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      code: this.value || '',
      validationError: null,
      showHelp: false,
      cmOptions: {
        tabSize: 2,
        mode: 'text/css',
        lineNumbers: true,
        lineWrapping: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        viewportMargin: Infinity,  // Auto-expand
      },
      cssExamples: [
        {
          title: this.$pgettext('form_configurator.css_example', 'Rounded inputs'),
          description: this.$pgettext('form_configurator.css_example', 'Make input fields more rounded'),
          code: `.form-control,
.form-select {
  border-radius: 20px !important;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Rounded button'),
          description: this.$pgettext('form_configurator.css_example', 'Make the submit button rounded'),
          code: `.btn.btn-pica-primary {
  border-radius: 25px !important;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Larger logo'),
          description: this.$pgettext('form_configurator.css_example', 'Increase the logo size'),
          code: `.logo {
  max-width: 400px !important;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Smaller logo'),
          description: this.$pgettext('form_configurator.css_example', 'Decrease the logo size'),
          code: `.logo {
  max-width: 150px !important;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Centered title'),
          description: this.$pgettext('form_configurator.css_example', 'Center and style the form title'),
          code: `.title {
  text-align: center;
  font-size: 28px;
  font-weight: 700;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Custom label style'),
          description: this.$pgettext('form_configurator.css_example', 'Change the appearance of field labels'),
          code: `.form-label {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Button with shadow'),
          description: this.$pgettext('form_configurator.css_example', 'Add shadow effect to the submit button'),
          code: `.btn.btn-pica-primary {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}
.btn.btn-pica-primary:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}`
        },
        {
          title: this.$pgettext('form_configurator.css_example', 'Input with border'),
          description: this.$pgettext('form_configurator.css_example', 'Add colored border to input fields'),
          code: `.form-control,
.form-select {
  border: 2px solid #e0e0e0 !important;
}
.form-control:focus,
.form-select:focus {
  border-color: var(--bs-primary) !important;
}`
        },
      ]
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.code) {
        this.code = newVal || ''
      }
    }
  },
  created() {
    this.debouncedEmit = debounce(this.emitChange, 300)
  },
  methods: {
    onInput(code) {
      const validation = this.validateCss(code)
      if (validation.valid) {
        this.validationError = null
        this.debouncedEmit(code)
      } else {
        this.validationError = validation.error
      }
    },
    emitChange(code) {
      this.$emit('input', code)
      this.$emit('change')
    },
    validateCss(css) {
      if (!css || css.trim() === '') {
        return {valid: true}
      }

      // Check for dangerous patterns
      for (const pattern of BLOCKED_PATTERNS) {
        if (pattern.test(css)) {
          return {
            valid: false,
            error: this.$pgettext('form_configurator.css_error', 'CSS contains disallowed content for security reasons')
          }
        }
      }

      // Basic syntax validation - check for balanced braces
      const openBraces = (css.match(/{/g) || []).length
      const closeBraces = (css.match(/}/g) || []).length
      if (openBraces !== closeBraces) {
        return {
          valid: false,
          error: this.$pgettext('form_configurator.css_error', 'CSS syntax error: unbalanced braces')
        }
      }

      return {valid: true}
    },
    insertExample(example) {
      const currentCode = this.code || ''
      const separator = currentCode.trim() ? '\n\n' : ''
      this.code = currentCode + separator + example.code
      this.onInput(this.code)
      this.showHelp = false
    }
  }
}
</script>

<style scoped>
.css-editor >>> .CodeMirror {
  min-height: 100px;
  height: auto;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  background: #fafafa;
}

.css-editor >>> .CodeMirror-focused {
  border-color: #321fdb;
  box-shadow: 0 0 0 0.2rem rgba(50, 31, 219, 0.25);
}

.css-editor >>> .CodeMirror-gutters {
  background: #f0f0f0;
  border-right: 1px solid #ddd;
}

.css-editor >>> .CodeMirror-linenumber {
  color: #999;
}

.invalid-feedback {
  color: #e55353;
  font-size: 0.875rem;
}

.css-examples {
  max-height: 60vh;
  overflow-y: auto;
}

.example-card {
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.example-card:hover {
  border-color: #321fdb;
  background: #f8f9ff;
}

.example-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.example-description {
  font-size: 13px;
  margin-bottom: 8px;
}

.example-code {
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  margin: 0;
  white-space: pre-wrap;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}
</style>
